import useSWRInfinite, { SWRInfiniteResponse } from "swr/infinite";

import { useCallback, useEffect, useMemo, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { transform } from "models";
import { useIntersectionObserver } from "usehooks-ts";

import { Loader } from "components/customElements";
import { baseFetcher } from "utils/baseFetcher";

export default function useInfiniteList(
  getKey: (_pageIndex: number, _previousPageData?: any) => any,
  options?: {
    fetcher?: (_endpoint: string) => Promise<any>;
    hasMore?: (_options: { data?: any[]; size: number }) => boolean;
    isEmpty?: (_options: { data?: any[]; size: number }) => boolean;
    loadingIndicator?: (_options: {
      data?: any[];
      size: number;
    }) => React.ReactNode;
    emptyIndicator?: () => React.ReactNode;
    endIndicator?: () => React.ReactNode;
    loadMore?: (_loadNextPage: () => void) => React.ReactNode;
    pagesRender?: (_data: any, _swr: SWRInfiniteResponse) => React.ReactNode;
    pageRender?: (_pageData: any, _index: number) => React.ReactNode;
    scrollThreshold?: number;
    swrOptions?: any;
    useEndRef?: boolean;
    inverse?: boolean;
    styles?: any;
    ref?: React.Ref<InfiniteScroll>;
    onSuccess?: ((_data: any[], _key: string) => void) | undefined;
    scrollableTarget?: React.ReactNode;
    manualLoad?: boolean;
  }
) {
  const swr = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.data?.length === 0) return null;

      return getKey(pageIndex, previousPageData);
    },
    options?.fetcher || baseFetcher,
    {
      revalidateFirstPage: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      ...(options?.swrOptions || {}),
      onSuccess: options?.onSuccess,
    }
  );
  const { data: rawData, isValidating, mutate, size, setSize } = swr;

  const data = rawData && transform(rawData);

  const hasMore = options?.hasMore
    ? options?.hasMore({ data, size })
    : !data || (!!data?.[size - 1] && !data?.[size - 1]?.errors);

  const isEmpty = options?.isEmpty
    ? options?.isEmpty({ data, size })
    : !data?.some((page: any) => page?.data?.length);

  const isRefreshing = isValidating && data && data.length === size;

  useEffect(() => {
    if (hasMore && !options?.swrOptions?.revalidateFirstPage) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prependItem = useCallback(
    (item) => {
      mutate([{ data: [item] }, ...(data || [])]);
    },
    [data, mutate]
  );

  const appendItem = useCallback(
    (item) => {
      const lastPage = data?.length > 0 ? data?.[data?.length - 1] : {};
      mutate(
        [
          ...(data?.slice(0, data?.length - 1) || []),
          { ...lastPage, data: [...(lastPage?.data || []), item] },
        ],
        { revalidate: false }
      );
    },
    [data, mutate]
  );

  const replaceItem = useCallback(
    (item) => {
      mutate([...(data || []), { data: [item] }]);
    },
    [data, mutate]
  );

  const loadNextPage = useCallback(() => {
    if (!isValidating && hasMore) {
      setSize(size + 1);
    }
  }, [hasMore, isValidating, setSize, size]);

  const endRef = useRef<HTMLDivElement>(null);
  const endRefObserver = useIntersectionObserver(endRef, {});
  useEffect(() => {
    if (
      !options?.manualLoad &&
      options?.useEndRef &&
      endRefObserver?.isIntersecting
    ) {
      loadNextPage();
    }
  }, [
    endRefObserver?.isIntersecting,
    hasMore,
    isValidating,
    options?.useEndRef,
    options?.manualLoad,
    loadNextPage,
  ]);

  const infiniteList = useMemo(() => {
    if (!options?.pageRender && !options?.pagesRender) return null;

    return (
      <>
        {options.inverse && <div ref={endRef} className="h-[2px] w-full"></div>}
        <InfiniteScroll
          ref={options.ref}
          dataLength={data?.length || 0}
          next={() =>
            !options.manualLoad && !options?.useEndRef && loadNextPage()
          }
          style={{ overflow: "visible", ...(options.styles || {}) }}
          hasMore={hasMore}
          loader={
            isValidating
              ? options?.loadingIndicator?.({ data, size }) || <Loader />
              : null
          }
          pullDownToRefresh={false}
          releaseToRefreshContent={false}
          scrollThreshold={options?.scrollThreshold || 0.8}
          inverse={options.inverse}
          endMessage={
            isValidating ? (
              options?.loadingIndicator?.({ data, size }) || <Loader />
            ) : !hasMore ? (
              isEmpty ? (
                options?.emptyIndicator?.() || <div>No data</div>
              ) : (
                options?.endIndicator?.() || <div></div>
              )
            ) : (
              <div></div>
            )
          }
          scrollableTarget={options.scrollableTarget}
        >
          {options?.pagesRender
            ? options?.pagesRender(data, swr)
            : data?.map((pageData: any, index: number) =>
                options?.pageRender?.(pageData, index)
              )}
        </InfiniteScroll>
        {!options.inverse && (
          <>
            {options.manualLoad &&
              hasMore &&
              (options?.loadMore?.(loadNextPage) || <div></div>)}
            <div ref={endRef} className="h-[2px] w-full"></div>
          </>
        )}
      </>
    );
  }, [data, hasMore, isEmpty, isValidating, loadNextPage, options, size, swr]);

  const rs = useMemo(
    () => ({
      data,
      isValidating,
      mutate,
      size,
      setSize,
      hasMore,
      isEmpty,
      isRefreshing,
      infiniteList,
      prependItem,
      appendItem,
      replaceItem,
    }),
    [
      data,
      hasMore,
      infiniteList,
      isEmpty,
      isRefreshing,
      isValidating,
      mutate,
      setSize,
      size,
      prependItem,
      appendItem,
      replaceItem,
    ]
  );

  return rs;
}
